import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import { categories, articles } from '@/mocks/data';

function ArticleSchema({ article, cat }: { article: typeof articles[0]; cat?: typeof categories[0] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '男人团',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://nantuan.com/article/${article.id}`,
    },
    articleSection: cat?.name || '',
    keywords: article.tags.join(','),
  };

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
  );
}

function renderMarkdown(content: string) {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={key++} className="font-heading text-2xl font-bold text-foreground-900 mt-10 mb-4">
          {line.replace('## ', '')}
        </h2>
      );
      i++;
      continue;
    }

    if (line.startsWith('### ')) {
      elements.push(
        <h3 key={key++} className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">
          {line.replace('### ', '')}
        </h3>
      );
      i++;
      continue;
    }

    if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(
        <p key={key++} className="font-semibold text-foreground-800 mt-6 mb-2">
          {line.replace(/\*\*/g, '')}
        </p>
      );
      i++;
      continue;
    }

    if (line.startsWith('- **') || line.startsWith('- ')) {
      const listItems: string[] = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('  - '))) {
        listItems.push(lines[i].replace(/^[\s-]+/, ''));
        i++;
      }
      elements.push(
        <ul key={key++} className="list-disc pl-6 my-3 space-y-1.5 text-foreground-700 leading-relaxed">
          {listItems.map((item, idx) => (
            <li key={idx}>{item.replace(/\*\*/g, '')}</li>
          ))}
        </ul>
      );
      continue;
    }

    if (line.match(/^\d+\.\s/)) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s/)) {
        listItems.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={key++} className="list-decimal pl-6 my-3 space-y-1.5 text-foreground-700 leading-relaxed">
          {listItems.map((item, idx) => (
            <li key={idx}>{item.replace(/\*\*/g, '')}</li>
          ))}
        </ol>
      );
      continue;
    }

    if (line.trim() === '') {
      i++;
      continue;
    }

    elements.push(
      <p key={key++} className="text-foreground-700 leading-relaxed mb-4">
        {line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}
      </p>
    );
    i++;
  }

  return elements;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <PageLayout breadcrumb={[{ label: '首页', path: '/' }, { label: '文章未找到' }]}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700">返回首页</Link>
        </div>
      </PageLayout>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <PageLayout
      breadcrumb={[
        { label: '首页', path: '/' },
        { label: cat?.name || '文章', path: cat ? `/category/${cat.slug}` : undefined },
        { label: article.title },
      ]}
    >
      <ArticleSchema article={article} cat={cat} />

      <article className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            {cat && (
              <Link
                to={`/category/${cat.slug}`}
                className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4"
              >
                {cat.name}
              </Link>
            )}
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i> 阅读约 {article.readTime} 分钟
              </span>
            </div>
          </div>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`男人团 - ${article.title}`}
              className="w-full h-[400px] object-cover object-top"
            />
          </div>

          <div className="prose-custom text-base">
            {renderMarkdown(article.content)}
          </div>

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-foreground-500 text-sm">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-14 pt-10 border-t border-background-200/70">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
                        {ra.title}
                      </h4>
                      <p className="text-foreground-400 text-xs mt-2">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </PageLayout>
  );
}